/**
 * ColumnsRenderer - Renders a flex-based columns container.
 *
 * Handles:
 * - COLUMNS: Side-by-side column layout with configurable widths
 * - Each column is a vertical stack (flex-direction: column)
 * - Children are filtered by parentPanelId (column index: "col-0", "col-1", etc.)
 *
 * Ported from the original MyVault application.
 */

import React from "react";
import { Box, Typography } from "@mui/joy";
import type { CanvasElement, ContainerElementConfig } from "../../../types";
import { ChildrenRenderer } from "../ChildrenRenderer";
import { ElementRenderer } from "../ElementRenderer";
import type { FieldState } from "../CanvasRenderer";

/**
 * Extended config for columns containers
 */
interface ColumnsConfig extends ContainerElementConfig {
  columnWidths?: string[]; // e.g., ["1fr", "2fr", "1fr"]
}

/**
 * Props for ColumnsRenderer
 */
interface ColumnsRendererProps {
  /** The container element */
  element: CanvasElement;
  /** All canvas elements (for child lookup) */
  allElements: CanvasElement[];
  /** Mode: edit or runtime */
  mode: "edit" | "runtime";
  /** Field state for form values */
  fieldState?: Record<string, FieldState>;
}

/**
 * Parse columnWidths to flex values.
 * "1fr" -> flex: 1, "2fr" -> flex: 2, etc.
 */
function parseFlexValue(width: string): number {
  if (!width) return 1;
  const match = width.match(/^(\d+(?:\.\d+)?)fr$/);
  if (match) {
    return parseFloat(match[1]);
  }
  // Default to equal width if format is unrecognized
  return 1;
}

/**
 * ColumnsRenderer - Renders a columns container with flex layout.
 */
export function ColumnsRenderer({
  element,
  allElements,
  mode,
  fieldState = {},
}: ColumnsRendererProps) {
  const config = element.props as unknown as ColumnsConfig;
  const title = config?.title || element.props?.label;
  const gap = config?.internalGrid?.gap ?? 16;

  // Get column widths, default to 2 equal columns
  const columnWidths = config?.columnWidths?.length
    ? config.columnWidths
    : ["1fr", "1fr"];

  // Render function for children
  const renderElement = (child: CanvasElement, isNested: boolean) => (
    <ElementRenderer
      key={child.id}
      element={child}
      allElements={allElements}
      isNested={isNested}
      mode={mode}
      fieldState={fieldState}
    />
  );

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: mode === "edit" ? "1px dashed" : "none",
        borderColor: "neutral.300",
        borderRadius: "md",
        p: title ? 0 : 1,
      }}
    >
      {title && (
        <Typography
          level="title-sm"
          fontWeight="lg"
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {title}
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: `${gap}px`,
          flexGrow: 1,
          p: title ? 2 : 1,
        }}
      >
        {columnWidths.map((width, index) => {
          const columnId = `col-${index}`;
          const flexValue = parseFlexValue(width);

          return (
            <Box
              key={columnId}
              sx={{
                flex: flexValue,
                display: "flex",
                flexDirection: "column",
                minWidth: 0, // Allow flex items to shrink below content size
              }}
            >
              <ChildrenRenderer
                elements={allElements}
                parentId={element.id}
                panelId={columnId}
                columns={config?.internalGrid?.columns ?? 12}
                gap={config?.internalGrid?.gap ?? 8}
                rowHeight={config?.internalGrid?.rowHeight ?? 60}
                internalGrid={config?.internalGrid}
                mode={mode}
                fieldState={fieldState}
                renderElement={renderElement}
                showEmptyPlaceholder={mode === "edit"}
                emptyPlaceholderText={
                  mode === "edit" ? `Column ${index + 1}` : undefined
                }
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default ColumnsRenderer;
